import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { ClearIcon } from "@mui/x-date-pickers";
import { useLazyGetAddressSelectQuery } from "../../../middleware/addressDataApi";
import { useAppSelector } from "../../../hooks/redux";
import { useTranslation } from "react-i18next";
import useScreenSize from "../../../hooks/useScreenSize";
import DropdownIcon from "../icons/DropdownIcon";
import "./style.css";

export const AddressInputInbuilt = ({
  label,
  disabled = false,
  onChange,
  error,
  value,
  shouldSetCoordinates,
  writeSearchDataToSessionStorage,
  storageValue,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const screenSize = useScreenSize();
  const [internalValue, setInternalValue] = useState(null);
  const { t } = useTranslation();

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const [getAddressSelect] = useLazyGetAddressSelectQuery();
  const { addressData } = useAppSelector((state) => state.addressData);
  const handleClearValue = () => {
    writeSearchDataToSessionStorage(storageValue, "");
    onChange("");
  };

  const getRequestNewValue = (newValue) => {
    const { data } = newValue || {};
    return {
      subdivision: data?.region_with_type || "",
      region: data?.region_with_type || "",
      area: data?.area_with_type || "",
      city: data?.city_with_type || data?.city || "",
      town: data?.settlement_with_type || "",
      street: data?.street_with_type || data?.street || "",
      building: data?.house || "",
      extension: data?.block || "",
      index: data?.postal_code || "",
      district: data?.federal_district || "",
      cityDistrict: data?.city_district_with_type || "",
      cityArea: data?.city_area || "",
      coordinates: shouldSetCoordinates
        ? {
            latitude: data?.geo_lat || 0,
            longitude: data?.geo_lon || 0,
          }
        : null,
    };
  };

  const formatDisplayValue = (val) => {
    if (!val) return "";

    const parts = [];
    if (val.region) parts.push(val.region);
    if (val.area && val.area !== val.region) parts.push(val.area);
    if (val.city && val.city !== val.region) parts.push(val.city);
    if (val.town && val.town !== val.city) parts.push(val.town);
    if (val.cityDistrict) parts.push(val.cityDistrict);
    if (val.cityArea) parts.push(val.cityArea);
    if (val.street) parts.push(val.street);
    if (val.building) parts.push(val.building);
    if (val.extension) parts.push(`к${val.extension}`);

    return parts.join(" ");
  };

  useEffect(() => {
    if (
      value &&
      (value.city ||
        value.street ||
        value.building ||
        value.index ||
        value.region ||
        value.town ||
        value.subdivision ||
        value.area ||
        value.cityDistrict ||
        value.cityArea)
    ) {
      const formattedValue = {
        data: {
          region_with_type: value.subdivision || value.region,
          area_with_type: value.area || "",
          city_with_type: value.city,
          city: value.city,
          settlement_with_type: value.town,
          street_with_type: value.street,
          street: value.street,
          house: value.building,
          block: value.extension,
          postal_code: value.index,
          federal_district: value.district,
          city_district_with_type: value.city_district || "",
          city_area: value.city_area || "",
          geo_lat: value.coordinates?.latitude,
          geo_lon: value.coordinates?.longitude,
        },
        unrestricted_value: "",
        value: formatDisplayValue(value),
      };
      setInternalValue(formattedValue);
      setInputValue(formatDisplayValue(value));
    } else {
      setInternalValue(null);
      setInputValue("");
    }
  }, [value]);

  useEffect(() => {
    let active = true;
    if (inputValue === "") {
      getAddressSelect("");
      return undefined;
    }

    if (active && inputValue !== formatDisplayValue(value)) {
      getAddressSelect(inputValue);
    }

    return () => {
      active = false;
    };
  }, [inputValue, getAddressSelect, value]);

  return (
    <Autocomplete
      id="address"
      getOptionLabel={(option) => option.value}
      ListboxProps={{ style: { maxHeight: "100px" } }}
      options={addressData?.suggestions || []}
      filterOptions={(x) => x}
      className="adress-filter-in-table"
      autoComplete
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      value={internalValue}
      inputValue={inputValue}
      filterSelectedOptions
      noOptionsText="Введите адрес"
      disabled={disabled}
      defaultValue={() =>
        value?.city
          ? {
              data: {},
              unrestricted_value: "",
              value: `${value.city} ${value.street} ${value.building}`,
            }
          : null
      }
      onChange={(event, newValue) => {
        if (newValue) {
          const requestValue = getRequestNewValue(newValue);
          onChange(requestValue);
          setInputValue(formatDisplayValue(requestValue));
          setInternalValue(newValue);
          writeSearchDataToSessionStorage && writeSearchDataToSessionStorage(storageValue, requestValue);
        }
      }}
      onInputChange={(event, newInputValue, reason) => {
        setInputValue(newInputValue);
        writeSearchDataToSessionStorage && writeSearchDataToSessionStorage(storageValue, value);
      }}
      renderInput={(params) => (
        <TextField
          className="adress-filter-table"
          placeholder="Адрес"
          {...params}
          sx={{
            color: "black",
            display: "flex",
            "& .MuiInputBase-root": {
              lineHeight: "24px",
              backgroundColor: "transparent",
              fontSize: screenSize.width < 1560 ? "14px" : "16px",
              padding: "0px !important",
            },
            "& .MuiOutlinedInput-input": {
              display: "flex",
              lineHeight: "24px",
              fontSize: screenSize.width < 1560 ? "14px" : "16px",
              color: "var(--headers)",
              border: "none",
            },
            "& .MuiInputBase-root .MuiOutlinedInput-input::placeholder": {
              color: "#4D5C6E  !important",
              fontWeight: "500",
            },
            "& fieldset": { border: "none" },
          }}
          label={t(label)}
          error={!!error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Box
                sx={{ display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }}
                onClick={() => {
                  value ? handleClearValue() : setIsOpen((prev) => !prev);
                }}
              >
                {value ? <ClearIcon sx={{ fontSize: 22 }} /> : <DropdownIcon />}
              </Box>
            ),
          }}
        />
      )}
    />
  );
};
